/**
 * EditorState - Centralized state management for the locale editor
 * 
 * This class manages:
 * - Current locale data (name, positions, props, walls)
 * - Selection state (selected prop, position, or wall)
 * - Undo/redo history
 * - UI state (zoom, mode, visibility flags)
 */

import type { PropData, Position, Wall } from './types';

// ============================================
// TYPES
// ============================================

export interface LocaleDataConfig {
  wallColor?: number;
  floorColor?: number;
  background?: number;
  grid?: {
    visible?: boolean;
    size?: number;
    color?: number;
    alpha?: number;
  };
}

/** Serializable prop data (no sprite reference) for undo/redo snapshots */
export interface PropSnapshot {
  key: string;
  x: number;
  y: number;
  scale: number;
  depth: number;
  flipX: boolean;
  angle: number;
}

export interface StateSnapshot {
  props: PropSnapshot[];  // Serializable, no sprite references
  positions: Record<string, Position>;
  walls: Wall[];
  timestamp: number;
}

export interface StateCallbacks {
  onPropsChange?: () => void;
  onSelectionChange?: () => void;
  onUndoRedoChange?: () => void;
  onLocaleChange?: () => void;
}

export const DEFAULT_WORLD = {
  width: 800,
  height: 450,
  wallColor: 0x8b7355,
  floorColor: 0xdeb887,
  background: 0x654321,
};

const MAX_UNDO_STATES = 50;

// ============================================
// EDITOR STATE CLASS
// ============================================

export class EditorState {
  // -------------------------
  // Locale Data
  // -------------------------
  
  /** Current locale identifier (e.g., "coffee_shop") */
  currentLocaleName: string = '';
  
  /** Current locale metadata */
  currentLocaleData: LocaleDataConfig = {};
  
  /** Named positions in the current locale */
  currentLocalePositions: Record<string, Position> = {};
  
  /** Props placed in the scene */
  private _props: PropData[] = [];
  
  /** Collision walls */
  walls: Wall[] = [];

  // -------------------------
  // Selection State
  // -------------------------
  
  /** Currently selected prop (if any) */
  private _selectedProp: PropData | null = null;
  
  /** Currently selected position name (if any) */
  private _selectedPosition: string | null = null;
  
  /** Currently selected wall index (if any) */
  selectedWall: number | null = null;
  
  /** Multiple selected props for batch operations */
  multiSelection: PropData[] = [];

  // -------------------------
  // Undo/Redo
  // -------------------------
  
  private undoStack: StateSnapshot[] = [];
  private redoStack: StateSnapshot[] = [];
  
  /** Flag to prevent saving state during undo/redo operations */
  isUndoRedoAction: boolean = false;

  // -------------------------
  // UI State
  // -------------------------
  
  /** Current zoom level (1 = 100%) */
  currentZoom: number = 1;
  
  /** Whether spawn point markers are visible */
  showSpawnPoints: boolean = false;
  
  /** Current editing mode */
  mode: 'select' | 'draw_wall' | 'pan' = 'select';
  
  /** Currently dragged palette item key */
  draggedPaletteItem: string | null = null;
  
  /** Whether positions have unsaved changes */
  positionsDirty: boolean = false;

  // -------------------------
  // Callbacks
  // -------------------------
  
  private callbacks: StateCallbacks;

  // ============================================
  // CONSTRUCTOR
  // ============================================

  constructor(callbacks: StateCallbacks = {}) {
    this.callbacks = callbacks;
  }

  // ============================================
  // PROPS MANAGEMENT
  // ============================================

  get props(): PropData[] {
    return this._props;
  }

  get propCount(): number {
    return this._props.length;
  }

  addProp(prop: PropData): void {
    this._props.push(prop);
    this.callbacks.onPropsChange?.();
  }

  removeProp(prop: PropData): void {
    const index = this._props.indexOf(prop);
    if (index !== -1) {
      this._props.splice(index, 1);
      if (this._selectedProp === prop) {
        this._selectedProp = null;
        this.callbacks.onSelectionChange?.();
      }
      this.callbacks.onPropsChange?.();
    }
  }

  clearProps(): void {
    // Clear in-place to maintain array reference for aliases
    this._props.length = 0;
    this._selectedProp = null;
    this.callbacks.onPropsChange?.();
    this.callbacks.onSelectionChange?.();
  }

  setProps(newProps: PropData[]): void {
    // Clear and repopulate in-place to maintain array reference for aliases
    this._props.length = 0;
    this._props.push(...newProps);
    this.callbacks.onPropsChange?.();
  }

  findPropBySprite(sprite: Phaser.GameObjects.Sprite): PropData | undefined {
    return this._props.find(p => p.sprite === sprite);
  }

  // ============================================
  // SELECTION MANAGEMENT
  // ============================================

  get selectedProp(): PropData | null {
    return this._selectedProp;
  }

  set selectedProp(prop: PropData | null) {
    if (this._selectedProp !== prop) {
      this._selectedProp = prop;
      this._selectedPosition = null;
      this.selectedWall = null;
      this.callbacks.onSelectionChange?.();
    }
  }

  get selectedPosition(): string | null {
    return this._selectedPosition;
  }

  set selectedPosition(name: string | null) {
    if (this._selectedPosition !== name) {
      this._selectedPosition = name;
      this._selectedProp = null;
      this.selectedWall = null;
      this.callbacks.onSelectionChange?.();
    }
  }

  deselectAll(): void {
    this._selectedProp = null;
    this._selectedPosition = null;
    this.selectedWall = null;
    this.multiSelection = [];
    this.callbacks.onSelectionChange?.();
  }

  // ============================================
  // POSITION MANAGEMENT
  // ============================================

  addPosition(name: string, x?: number, y?: number): boolean {
    if (this.currentLocalePositions[name]) {
      return false; // Already exists
    }
    
    this.currentLocalePositions[name] = {
      x: x ?? 400,
      y: y ?? 225,
    };
    this.positionsDirty = true;
    return true;
  }

  updatePosition(name: string, x: number, y: number): void {
    if (this.currentLocalePositions[name]) {
      this.currentLocalePositions[name].x = x;
      this.currentLocalePositions[name].y = y;
      this.positionsDirty = true;
    }
  }

  deletePosition(name: string): boolean {
    if (this.currentLocalePositions[name]) {
      delete this.currentLocalePositions[name];
      if (this._selectedPosition === name) {
        this._selectedPosition = null;
        this.callbacks.onSelectionChange?.();
      }
      this.positionsDirty = true;
      return true;
    }
    return false;
  }

  getPositionNames(): string[] {
    return Object.keys(this.currentLocalePositions).sort();
  }

  // ============================================
  // LOCALE MANAGEMENT
  // ============================================

  createNewLocale(name: string, width: number = 800, height: number = 450): void {
    this.currentLocaleName = name;
    this.currentLocaleData = {
      wallColor: DEFAULT_WORLD.wallColor,
      floorColor: DEFAULT_WORLD.floorColor,
      background: DEFAULT_WORLD.background,
    };
    this.currentLocalePositions = {
      center: { x: width / 2, y: height / 2 },
      entrance: { x: width / 2, y: height - 50 },
    };
    // Clear in-place to maintain array reference
    this._props.length = 0;
    this.walls = [];
    this._selectedProp = null;
    this._selectedPosition = null;
    this.selectedWall = null;
    
    // Clear undo/redo on new locale
    this.undoStack = [];
    this.redoStack = [];
    
    this.callbacks.onLocaleChange?.();
    this.callbacks.onPropsChange?.();
    this.callbacks.onSelectionChange?.();
    this.callbacks.onUndoRedoChange?.();
  }

  loadLocale(
    name: string,
    data: {
      localeData: LocaleDataConfig;
      positions: Record<string, Position>;
      props: PropData[];
      walls: Wall[];
    }
  ): void {
    this.currentLocaleName = name;
    this.currentLocaleData = data.localeData;
    this.currentLocalePositions = data.positions;
    // Clear and repopulate in-place to maintain array reference
    this._props.length = 0;
    this._props.push(...data.props);
    this.walls = data.walls;
    this._selectedProp = null;
    this._selectedPosition = null;
    this.selectedWall = null;
    
    // Clear undo/redo on locale load
    this.undoStack = [];
    this.redoStack = [];
    
    this.callbacks.onLocaleChange?.();
    this.callbacks.onPropsChange?.();
    this.callbacks.onSelectionChange?.();
    this.callbacks.onUndoRedoChange?.();
  }

  // ============================================
  // UNDO/REDO
  // ============================================

  /**
   * Save current state to undo stack
   * Call this BEFORE making a change
   */
  saveState(_action?: string): void {
    if (this.isUndoRedoAction) return;
    
    const snapshot: StateSnapshot = {
      props: this._props.map(p => ({
        key: p.key,
        x: p.x,
        y: p.y,
        scale: p.scale,
        depth: p.depth,
        flipX: p.flipX,
        angle: p.angle,
        // No sprite reference - snapshots are serializable
      })),
      positions: JSON.parse(JSON.stringify(this.currentLocalePositions)) as Record<string, Position>,
      walls: JSON.parse(JSON.stringify(this.walls)) as Wall[],
      timestamp: Date.now(),
    };
    
    this.undoStack.push(snapshot);
    
    // Limit undo stack size
    if (this.undoStack.length > MAX_UNDO_STATES) {
      this.undoStack.shift();
    }
    
    // Clear redo stack on new action
    this.redoStack = [];
    
    this.callbacks.onUndoRedoChange?.();
  }
  
  /** Create a snapshot of current state (for undo/redo) */
  private createSnapshot(): StateSnapshot {
    return {
      props: this._props.map(p => ({
        key: p.key,
        x: p.x,
        y: p.y,
        scale: p.scale,
        depth: p.depth,
        flipX: p.flipX,
        angle: p.angle,
      })),
      positions: JSON.parse(JSON.stringify(this.currentLocalePositions)) as Record<string, Position>,
      walls: JSON.parse(JSON.stringify(this.walls)) as Wall[],
      timestamp: Date.now(),
    };
  }

  canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  popUndo(): StateSnapshot | null {
    if (this.undoStack.length === 0) return null;
    
    // Save current state to redo stack
    this.redoStack.push(this.createSnapshot());
    
    const snapshot = this.undoStack.pop()!;
    this.callbacks.onUndoRedoChange?.();
    return snapshot;
  }

  popRedo(): StateSnapshot | null {
    if (this.redoStack.length === 0) return null;
    
    // Save current state to undo stack
    this.undoStack.push(this.createSnapshot());
    
    const snapshot = this.redoStack.pop()!;
    this.callbacks.onUndoRedoChange?.();
    return snapshot;
  }

  clearHistory(): void {
    this.undoStack = [];
    this.redoStack = [];
    this.callbacks.onUndoRedoChange?.();
  }

  // ============================================
  // SERIALIZATION
  // ============================================

  toJSON(): {
    localeName: string;
    localeData: LocaleDataConfig;
    positions: Record<string, Position>;
    props: Array<Omit<PropData, 'sprite'>>;
    walls: Wall[];
  } {
    return {
      localeName: this.currentLocaleName,
      localeData: this.currentLocaleData,
      positions: this.currentLocalePositions,
      props: this._props.map(p => ({
        key: p.key,
        x: p.x,
        y: p.y,
        scale: p.scale,
        depth: p.depth,
        flipX: p.flipX,
        angle: p.angle,
      })),
      walls: this.walls,
    };
  }
}
